import React, { FC, useContext, useMemo, useState } from "react";
import { Font,  Page, Text, View, Document, StyleSheet, Image, pdf } from "@react-pdf/renderer";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import zioncom from 'images/system/zioncom.png'
import { App, Button, ButtonProps, notification } from "antd";
import { numFormmater } from "@/pages/Requirement/util";
import JsBarcode from "jsbarcode";
import convertTo2DArray from "@/utils/convert2double";
import { getFileUrl } from "@/apis/file";
import { DictContext } from "@/layout/context/DictContext";
import { OrderItemV2 } from "@/types/inventoryV2";
import { PurchaseOrder, PurchaseOrderItem } from "@/types/purchase";
import truncateString from "@/utils/stringOver";
import { Product } from "@/types/product";
import { getSourceNumber } from "@/utils/getInventorySourceNo";
import fontSrc from '@/assets/fonts/msyh.ttf';
import vnSrc from '@/assets/fonts/NotoSans-Regular.ttf';
import krSrc from '@/assets/fonts/NotoSansKR-Regular.otf';

// 注册支持中文的字体（重复注册幂等；若启动预热失败，首次渲染时在此兜底加载）
Font.register({
  family: "NotoSansSC",
  src: fontSrc
});

Font.register({
  family: "VnFontSrc",
  src: vnSrc
});

Font.register({
  family: "KoFontSrc",
  src: krSrc
});

// 定义 PDF 文档样式
const styles = StyleSheet.create({
  // 页面样式
  page: {
    position: "relative",
    fontFamily: ["VnFontSrc", "KoFontSrc", "NotoSansSC"],
    padding: 28
  },

  // 表格样式
  table: {
    display: "table" as any,
    width: "100%",
    borderStyle: "solid",
    borderLeftWidth: 1,
    borderTopWidth: 1,
    borderRightWidth: 0,
    borderBottomWidth: 0,
    borderColor: "black"
  },
  tableRow: {
    flexDirection: "row"
  },

  // 单元格基础样式
  tableCol: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderStyle: "solid",
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    borderColor: "black",
    padding: 2,
    fontSize: 6,
    letterSpacing: -1,
    color: 'black'
  },

  // 表头单元格
  tableTopTitle: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderStyle: "solid",
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    borderColor: "black",
    padding: 2,
    fontSize: 6,
    letterSpacing: -1,
    color: 'black'
  },

  // 表头文本
  tableHeaderCell: {
    fontFamily: ["VnFontSrc", "KoFontSrc", "NotoSansSC"],
    fontSize: 6
  },

  // 合并单元格
  mergeCell: {
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    padding: 0
  },

  // 文本样式
  fontStyle: {
    textAlign: 'center'
  },

  // 页面标题
  pageTitle: {
    fontSize: 14,
    textAlign: 'center',
    letterSpacing: 0,
    paddingTop: 8,
    paddingBottom: 16
  },

  // 标题区域容器
  titleSection: {
    position: 'relative' as any,
    marginBottom: 8
  },

  // 右上角条码样式
  titleBarcode: {
    position: 'absolute' as any,
    right: 0,
    top: 0,
    flexDirection: 'column',
    alignItems: 'center'
  },

  // 条码文字
  barcodeText: {
    fontSize: 5,
    marginTop: -3
  },

  // 底部信息样式
  bottomInfo: {
    width: "100%",
    flexDirection: "row",
    marginTop: 12,
    paddingHorizontal: 24,
    paddingBottom: 24
  },
  bottomItem: {
    flex: 1,
    textAlign: "left"
  },
  bottomText: {
    fontSize: 8
  },

  // 页脚
  footer: {
    position: "absolute",
    bottom: 30,
    left: 0,
    right: 0,
    fontSize: 6,
  },

  footerItem: { width: '33.3%', fontSize: 6 },
  headerItem: { marginRight: 30, fontSize: 6, textAlign: 'left' }
});

// 填充空格
function getBlankWord(word: string, type = false) {
  if (type) return String(word).replace(/./g, (word) => word + ' ');
  else return String(word).replace(/./g, (word) => word + '');
}

type CodeUrlOrder = OrderItemV2 & {
  codeStr: string;
  codeUrl: string;
}

interface BatchPurchaseQCPrintProps extends ButtonProps {
  selectedRows: Array<PurchaseOrderItem>;
  disabled: boolean;
  onStart: () => void;
  onEnd: () => void;
  clickValid?: () => boolean
}

// 生成 PDF 下载链接
const BatchPurchaseQCPrint: FC<BatchPurchaseQCPrintProps> = ({
  selectedRows, disabled, onStart, onEnd, clickValid = () => true, ...restProps
}) => {
  const { printSetting } = useContext(DictContext);

  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(false)

  const handleCode: (orderNo: string, lot: string) => Promise<{ codeUrl: string, codeStr: string }> = (orderNo: string, lot) => {
    return new Promise((resolve, reject) => {
      const canvasDom = document.createElement("canvas")
      JsBarcode(canvasDom, orderNo, {
        format: "CODE128",
        width: 10,
        height: 28,
        displayValue: false,
        margin: 0,
        marginBottom: 8
      })
      canvasDom.toBlob((blob) => {
        canvasDom.remove()
        if (blob) resolve({ codeUrl: URL.createObjectURL(blob), codeStr: lot })
        else reject("条码生成失败")
      }, 'image/png')

    })
  }

  const { picUrl, name } = useMemo(() => {
    let [picUrl, name] = ['', ''];
    if (printSetting?.length > 0) {
      printSetting.forEach(item => {
        if (item.key === "companyName" && item.value) name = item.value
        if (item.key === "companyLogo" && item.value) picUrl = getFileUrl(item.value) || ''
      })
    }

    return { picUrl, name };

  }, [printSetting])

  const getReceiverInfo = (order: PurchaseOrder | null) => {
    let supplierPrint = ""
    let mobilePrint = ""
    let recver = ""
    supplierPrint = order?.receiver || ''
    mobilePrint = order?.receiverMobile || ''
    recver = order?.receiverContact || ''
    return {
      supplierPrint,
      mobilePrint,
      recver
    }
  }

  const MyDocument = (selRows: CodeUrlOrder[]) => {
    let seq = 0;
    return (
      <Document>
        <Page size="A4" orientation="landscape" style={{ ...styles.page }}>
          <View fixed>
            {/* 标题部分 */}
            <View><Text style={{ ...styles.fontStyle, ...styles.pageTitle }}>{name ?? '吉翁科技（深圳）有限公司'} - - - ({`${t('purchase.checkoutOrder', { lng: "zh" })}/${t('purchase.checkoutOrder', { lng: "vn" })}`})</Text></View>
          </View>
          <View style={{ ...styles.table, flexShrink: 0 }}>
            {/* 表头 - 第一行信息 */}
            <View style={{ flexDirection: 'row' }} fixed render={({ pageNumber }) => {
              return pageNumber === 1 ? null : <View style={{ height: 1, backgroundColor: "#000", width: "100%" }} />
            }}>
            </View>
            <View style={{ flexDirection: 'row' }} fixed>
              <View style={{ ...styles.tableTopTitle, borderTopWidth: seq === 0 ? 0 : 1, width: '28%', textAlign: 'left' }}><Text style={{ width: "100%" }}>{getBlankWord(`${t('purchase.sendTimePrint', { lng: "zh" })}(${t('purchase.sendTimePrint', { lng: "vn" })}):`, true)}{getBlankWord(selRows[0]?.order.preTime ? dayjs(selRows[0]?.order.preTime).format('YYYY-MM-DD') : '', true)}</Text></View>
              <View style={{ ...styles.tableTopTitle, borderTopWidth: seq === 0 ? 0 : 1, width: '28%', textAlign: 'left' }}><Text style={{ width: "100%" }}>{getBlankWord(`${t('purchase.supplierPrint', { lng: "zh" })}(${t('purchase.supplierPrint', { lng: "vn" })}):` + getReceiverInfo((selRows[0]?.source as PurchaseOrderItem)?.order || null).supplierPrint, true)}</Text></View>
              <View style={{ ...styles.tableTopTitle, borderTopWidth: seq === 0 ? 0 : 1, width: '30%', textAlign: 'left' }}><Text style={{ width: "100%" }}>{getBlankWord(`${t('purchase.mobilePrint', { lng: "zh" })}(${t('purchase.mobilePrint', { lng: "vn" })}):` + getReceiverInfo((selRows[0]?.source as PurchaseOrderItem)?.order || null).mobilePrint, true)}</Text></View>
              <View style={{ ...styles.tableTopTitle, borderTopWidth: seq === 0 ? 0 : 1, width: '14%', textAlign: 'left' }}><Text style={{ width: "100%" }}>{getBlankWord(`${t('purchase.recver', { lng: "zh" })}(${t('purchase.recver', { lng: "vn" })}):` + getReceiverInfo((selRows[0]?.source as PurchaseOrderItem)?.order || null).recver, true)}</Text></View>
            </View>
            {/* 表头 - 第二行列标题 */}
            <View style={{ flexDirection: 'row' }} fixed>
              <View style={{ ...styles.tableCol, width: '6%', borderTopWidth: 0 }}>
                <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell }}>{getBlankWord(t('purchase.seq', { lng: "zh" }))}</Text>
                <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell }}>{getBlankWord(t('purchase.seq', { lng: "vn" }))}</Text>
              </View>
              <View style={{ ...styles.tableCol, width: '22%' }}>
                <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell, letterSpacing: 0 }}>{t('purchase.sendNum', { lng: "zh" })}</Text>
                <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell, letterSpacing: 0 }}>{t('purchase.sendNum', { lng: "vn" })}</Text>
              </View>
              <View style={{ ...styles.tableCol, width: '12%' }}>
                <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell, letterSpacing: 0 }}>{getBlankWord(t('purchase.printCode', { lng: "zh" }))}</Text>
                <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell, letterSpacing: 0 }}>{getBlankWord(t('purchase.printCode', { lng: "vn" }))}</Text>
              </View>
              <View style={{ ...styles.tableCol, width: '8%' }}>
                <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell, letterSpacing: 0 }}>{getBlankWord(t('purchase.printName', { lng: "zh" }))}</Text>
                <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell, letterSpacing: 0 }}>{getBlankWord(t('purchase.printName', { lng: "vn" }))}</Text>
              </View>
              <View style={{ ...styles.tableCol, width: '20%' }}>
                <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell, letterSpacing: 0 }}>{getBlankWord(t('purchase.printModel', { lng: "zh" }))}</Text>
                <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell, letterSpacing: 0 }}>{getBlankWord(t('purchase.printModel', { lng: "vn" }))}</Text>
              </View>
              <View style={{ ...styles.tableCol, width: '4%' }}>
                <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell, letterSpacing: 0 }}>{getBlankWord(t('purchase.printUnit', { lng: "zh" }))}</Text>
                <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell, letterSpacing: 0 }}>{getBlankWord(t('purchase.printUnit', { lng: "vn" }))}</Text>
              </View>
              <View style={{ ...styles.tableCol, width: '7%' }}>
                <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell, letterSpacing: 0 }}>{getBlankWord(t('purchase.printNum', { lng: "zh" }))}</Text>
                <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell, letterSpacing: 0 }}>{getBlankWord(t('purchase.printNum', { lng: "vn" }))}</Text>
              </View>
              <View style={{ ...styles.tableCol, width: '7%' }}>
                <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell, letterSpacing: 0 }}>{getBlankWord(t('purchase.qcNum', { lng: "zh" }))}</Text>
                <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell, letterSpacing: 0 }}>{getBlankWord(t('purchase.qcNum', { lng: "vn" }))}</Text>
              </View>
              <View style={{ ...styles.tableCol, width: '7%' }}>
                <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell, letterSpacing: 0 }}>{getBlankWord(t('purchase.qcNo', { lng: "zh" }))}</Text>
                <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell, letterSpacing: 0 }}>{getBlankWord(t('purchase.qcNo', { lng: "vn" }))}</Text>
              </View>
              <View style={{ ...styles.tableCol, width: '7%' }}>
                <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell, letterSpacing: 0 }}>{getBlankWord(t('purchase.qcOk', { lng: "zh" }))}</Text>
                <Text style={{ ...styles.fontStyle, ...styles.tableHeaderCell, letterSpacing: 0 }}>{getBlankWord(t('purchase.qcOk', { lng: "vn" }))}</Text>
              </View>
            </View>
            {/* 表身 */}
            {selRows.map((item, rowIndex) => (
              <View style={styles.tableRow} key={rowIndex} wrap={false}>
                <View style={{ ...styles.tableCol, width: '6%' }}><Text style={styles.fontStyle}>{getBlankWord(++seq + '')}</Text></View>
                <View style={{ ...styles.tableCol, width: '22%', flexDirection: "column" }}>
                  {item.codeUrl && <Image src={item.codeUrl} style={{ width: "90%", height: 28 }} />}
                  <Text style={{ ...styles.fontStyle, letterSpacing: 0, fontSize: !!item.codeUrl ? 5 : 6, marginTop: !!item.codeUrl ? -6 : 0 }}>{item.codeStr}</Text>
                  <Text style={{ ...styles.fontStyle, letterSpacing: 0, fontSize: !!item.codeUrl ? 5 : 6, marginTop: -1 }}>{getSourceNumber(item)}</Text>
                </View>
                <View style={{ ...styles.tableCol, width: '12%' }}><Text style={{ ...styles.fontStyle, letterSpacing: -1 }}>{getBlankWord(item?.product?.code || '', true)}</Text></View>
                <View style={{ ...styles.tableCol, width: '8%' }}><Text style={{ ...styles.fontStyle, textAlign: 'left', letterSpacing: -1 }}>{getBlankWord(item?.product?.name || '', true)}</Text></View>
                <View style={{ ...styles.tableCol, width: '20%' }}><Text style={{ ...styles.fontStyle, textAlign: 'left', width: "100%", letterSpacing: -1 }}>{getBlankWord(truncateString(item?.product?.model || '', 38), true)}</Text></View>
                <View style={{ ...styles.tableCol, width: '4%' }}><Text style={{ ...styles.fontStyle, textAlign: 'left', width: "100%", letterSpacing: -1 }}>{getBlankWord(item?.product?.uom || '')}</Text></View>
                <View style={{ ...styles.tableCol, width: '7%' }}><Text style={{ ...styles.fontStyle, textAlign: 'left', width: "100%", letterSpacing: -1 }}>{getBlankWord(numFormmater(Number(item.count)), true)}</Text></View>
                <View style={{ ...styles.tableCol, width: '7%' }}></View>
                <View style={{ ...styles.tableCol, width: '7%' }}></View>
                <View style={{ ...styles.tableCol, width: '7%' }}></View>
              </View>
            ))}
          </View>

          {/* 底部签名区 */}
          <View style={styles.bottomInfo} fixed>
            <View style={styles.bottomItem}>
              <Text style={styles.bottomText}>{`${t('purchase.qc', { lng: "zh" })}(${t('purchase.qc', { lng: "vn" })})`}</Text>
            </View>
            <View style={styles.bottomItem}>
              <Text style={styles.bottomText}>{`${t('purchase.pg', { lng: "zh" })}(${t('purchase.pg', { lng: "vn" })})`}</Text>
            </View>
            <View style={styles.bottomItem}>
              <Text style={styles.bottomText}>{`${t('purchase.cg', { lng: "zh" })}(${t('purchase.cg', { lng: "vn" })})`}</Text>
            </View>
          </View>

          {/* 左上角Logo */}
          <Image fixed src={picUrl || zioncom} style={{
            position: "absolute",
            top: 24,
            left: 30,
            flexGrow: 1,
            height: 30,
            objectFit: "scale-down"
          }} />
          {/* 添加分页 */}
          <Text fixed style={{ ...styles.fontStyle, ...styles.footer, zIndex: 99 }} render={({ pageNumber, totalPages }) => {
            return `P ${pageNumber} / ${totalPages}`
          }}>
          </Text>
        </Page>
      </Document>
    )
  }

  const handlePDF = async () => {
    if (selectedRows.length <= 0) {
      return notification.error({
        placement: 'top',
        message: t('common.addError'),
        description: t('purchase.printSelectTip'),
      });
    }
    let valid = clickValid();
    if (!valid) return

    setLoading(true)
    onStart()

    try {
      // 从 PurchaseOrderItem 中提取所有 inventoryItems，并把每个 OrderItemV2 的 source 赋值为对应的 PurchaseOrderItem
      let allInventoryItems: OrderItemV2[] = [];
      selectedRows.forEach(purItem => {
        if (purItem.inventoryItems && purItem.inventoryItems.length > 0) {
          // 为每个 OrderItemV2 设置 source 为当前 PurchaseOrderItem
          const itemsWithSource = purItem.inventoryItems.map(item => ({
            ...item,
            product: purItem.product as Product,
            source: purItem
          }));
          allInventoryItems = allInventoryItems.concat(itemsWithSource as OrderItemV2[]);
        }
      });

      // 检查是否有有效的数据
      if (allInventoryItems.length === 0) {
        setLoading(false)
        onEnd()
        return notification.error({
          placement: 'top',
          message: t('common.addError'),
          description: t('purchase.printSelectTip'),
        });
      }

      // 生成条码
      let codeRows: CodeUrlOrder[] = [];
      for (let i = 0; i < allInventoryItems.length; i++) {
        const barcodeContent = allInventoryItems[i]?.order?.number;
        const displayText = allInventoryItems[i]?.order?.number;

        if (barcodeContent) {
          let codeObj = await handleCode(barcodeContent, displayText);
          let codeRow: CodeUrlOrder = {
            ...allInventoryItems[i],
            codeStr: codeObj.codeStr,
            codeUrl: codeObj.codeUrl
          }
          codeRows.push({ ...codeRow })
        }
      }

      let pageEle = MyDocument(codeRows)
      if (!pageEle) return notification.error({ placement: 'top', message: t('purchase.printErr') })
      // Generate PDF as Blob in the browser
      const blob = await pdf(pageEle).toBlob();
      const pdfUrl = URL.createObjectURL(blob);
      // 创建一个 iframe 并将 PDF URL 作为其源
      const iframe = document.createElement('iframe');

      iframe.style.position = 'absolute';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = 'none';
      iframe.src = pdfUrl;

      // 将 iframe 添加到 body 中
      document.body.appendChild(iframe);
      iframe.onload = () => {
        try {
          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();
        } catch (error) {
          console.error('打印失败:', error);
        } finally {
          setLoading(false)
          onEnd()
        }
      };

    } catch (error) {
      console.error('打印失败:', error);
      setLoading(false)
      onEnd()
    }
  };

  return (
    <Button type="primary" onClick={handlePDF} loading={loading} disabled={disabled} {...restProps}>{t('common.printInfoQC')}</Button>
  );
};

export default BatchPurchaseQCPrint;
